import { useEffect, useState, useCallback, useRef } from 'react';
import { Icon } from '@iconify/react';
import { MENU, SUBMENU } from '@/constants/navBar';

interface PageNavBarProps {
  onMobileMenuToggle?: (isOpen: boolean) => void;
}

const PageNavBar = ({ onMobileMenuToggle }: PageNavBarProps) => {
  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);
  const [curMenu, setCurMenu] = useState<string | null>(null);
  const [curSubMenu, setCurSubMenu] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Helper function to get parent menu for a section
  const getParentMenu = (section: string): string | null => {
    for (const [menu, submenus] of Object.entries(SUBMENU)) {
      if (submenus.includes(section)) {
        return menu;
      }
    }
    return null;
  };

  // Helper function to check if a menu should be highlighted
  const isMenuActive = (menu: string): boolean => {
    if (activeSection === menu) return true;
    return getParentMenu(activeSection || '') === menu;
  };

  const SetMenu = (event: CustomEvent) => {
    setCurMenu(event.detail.menu);
    setCurSubMenu(event.detail.subMenu);
  };

  useEffect(() => {
    document.addEventListener('onChangeMenuColor', SetMenu as EventListener);

    return () => {
      document.removeEventListener(
        'onChangeMenuColor',
        SetMenu as EventListener
      );
    };
  }, []);

  // Scroll detection for active section highlighting
  useEffect(() => {
    const handleScroll = () => {
      // All possible sections based on the navigation structure and page content
      const allSections = [
        'Home',
        'Product',
        'Technology',
        'Resources',
        'Team',
        'Contact',
        'Conjunction Briefing',
        'Building Trust',
        'Partners',
      ];

      const scrollPosition = window.scrollY + window.innerHeight / 3; // Use 1/3 from top
      let currentSection = null;
      let maxOverlap = 0;

      // Find the section with maximum overlap with viewport
      allSections.forEach(sectionId => {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          const elementTop = rect.top + window.scrollY;
          const elementBottom = elementTop + rect.height;

          // Calculate overlap with viewport
          const viewportTop = window.scrollY;
          const viewportBottom = window.scrollY + window.innerHeight;

          const overlapStart = Math.max(elementTop, viewportTop);
          const overlapEnd = Math.min(elementBottom, viewportBottom);
          const overlap = Math.max(0, overlapEnd - overlapStart);

          if (overlap > maxOverlap && overlap > 100) {
            // Minimum 100px overlap
            maxOverlap = overlap;
            currentSection = sectionId;
          }
        }
      });

      setActiveSection(currentSection);
    };

    // Initial check
    handleScroll();

    // Add scroll listener with throttling for better performance
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledScroll);
    return () => window.removeEventListener('scroll', throttledScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node)
      ) {
        setIsMobileMenuOpen(false);
        onMobileMenuToggle?.(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen, onMobileMenuToggle]);

  const handleNavClick = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    const page = document.getElementById(id);

    if (page) {
      page.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
      setExpandedMenu(null);
      onMobileMenuToggle?.(false);
    }
  };

  const toggleMobileMenu = useCallback(() => {
    const newState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newState);
    onMobileMenuToggle?.(newState);
  }, [isMobileMenuOpen, onMobileMenuToggle]);

  const toggleAccordion = (menu: string) => {
    setExpandedMenu(expandedMenu === menu ? null : menu);
  };

  return (
    <>
      {/* Mobile Hamburger Button */}
      <button
        className='xl:hidden text-white p-2'
        onClick={toggleMobileMenu}
        aria-label='Toggle menu'
      >
        <Icon icon='ci:hamburger-lg' width='32' height='32' />
      </button>

      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <div
          ref={mobileMenuRef}
          className='xl:hidden fixed top-[3.5rem] left-0 right-0 bg-black border-t border-gray-800 z-20 max-h-[calc(100vh-4.5rem)] overflow-y-auto'
        >
          <nav className='flex flex-col'>
            {MENU.map((menu, index) => {
              const isExpanded = expandedMenu === menu;
              const isCurrent = curMenu === menu || isMenuActive(menu);

              // Product menu - no dropdown
              if (menu === 'Product') {
                return (
                  <div
                    key={`${menu}${index}`}
                    className='border-b border-gray-800'
                  >
                    <button
                      className={`w-full flex items-center justify-between px-6 py-4 text-left text-lg font-bold ${
                        isCurrent ? 'text-[#fccb15]' : 'text-white'
                      }`}
                      onClick={e => handleNavClick(e, 'Product')}
                    >
                      {menu}
                    </button>
                  </div>
                );
              }

              return (
                <div
                  key={`${menu}${index}`}
                  className='border-b border-gray-800'
                >
                  <button
                    className={`w-full flex items-center justify-between px-6 py-4 text-left text-lg font-bold ${
                      isCurrent ? 'text-[#fccb15]' : 'text-white'
                    }`}
                    onClick={() => toggleAccordion(menu)}
                  >
                    {menu}
                    <Icon
                      icon={isExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                      width='24'
                      height='24'
                    />
                  </button>
                  {isExpanded && (
                    <div className='bg-gray-900'>
                      {SUBMENU[menu].map((subMenu, idx) => {
                        const isSubMenuCurrent =
                          curSubMenu === subMenu || activeSection === subMenu;

                        return (
                          <button
                            key={`${subMenu}${idx}`}
                            className={`w-full text-left px-8 py-3 text-base font-medium ${
                              isSubMenuCurrent
                                ? 'text-[#fccb15]'
                                : 'text-white hover:text-[#fccb15]'
                            }`}
                            onClick={e => handleNavClick(e, subMenu)}
                          >
                            {subMenu}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            {/* <button className='w-full px-6 py-4 text-left text-lg font-bold text-white border-b border-gray-800'>
              Login
            </button> */}
          </nav>
        </div>
      )}

      {/* Desktop Menu */}
      <nav className='hidden xl:block w-[40rem] ml-6'>
        <ul className='flex items-center justify-center gap-8 p-0'>
          {MENU.map((menu, index) => {
            const isHovered = hoveredMenu === menu;
            const isCurrent = curMenu === menu || isMenuActive(menu);
            const isActive = isHovered || isCurrent;

            // Product menu - no dropdown
            if (menu === 'Product') {
              return (
                <div
                  key={`${menu}${index}`}
                  className='relative flex flex-col items-center justify-center text-xl font-bold box-border cursor-pointer no-underline'
                  onClick={e => handleNavClick(e, 'Product')}
                >
                  <div
                    className={`${isCurrent ? 'text-[#fccb15]' : 'text-white'}`}
                  >
                    {menu}
                  </div>
                </div>
              );
            }

            return (
              <div
                key={`${menu}${index}`}
                className='relative flex flex-col items-center justify-center text-xl font-bold box-border cursor-pointer no-underline'
                onClick={e => handleNavClick(e, SUBMENU[menu][0])}
                onMouseEnter={() => setHoveredMenu(menu)}
                onMouseLeave={() => setHoveredMenu(null)}
              >
                <div
                  className={`${isActive ? 'text-[#fccb15]' : 'text-white'}`}
                >
                  {menu}
                </div>
                {isHovered && (
                  <div className='absolute top-full left-[-0.5rem] w-40 p-2 flex flex-col gap-1 bg-black rounded-[10px] z-10 border border-[#333333]'>
                    {SUBMENU[menu].map((subMenu, idx) => {
                      const isSubMenuCurrent =
                        curSubMenu === subMenu || activeSection === subMenu;

                      return (
                        <div
                          key={`${subMenu}${idx}`}
                          className={`text-base font-medium cursor-pointer no-underline hover:text-[#fccb15] ${
                            isSubMenuCurrent ? 'text-[#fccb15]' : 'text-white'
                          }`}
                          onClick={e => handleNavClick(e, subMenu)}
                        >
                          {subMenu}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </ul>
      </nav>
    </>
  );
};

export default PageNavBar;
